<template>
  <!-- Try it: flip the builder's "Signing order" switch, then sign as each person and watch who is
       emailed when. Labels are the app's (app_en.arb: contractsRecipientOrder, ctbSigningOrderOnHint,
       ctbSigningOrderOffHint, contractActivityInvited / Signed / Sealed / CompletionDelivered,
       contractsRoleSigner, contractSignerCountersigner, ctbRoleCc, contractsRoleSigner) and the
       signer page's ("Signed", "It isn't your turn yet"). People are sample data. Every row and
       both hints are always drawn, so nothing changes height. -->
  <div class="rp sdo">
    <div class="sdo-head">
      <span class="sdo-title"><b>Who signs</b><small translate="no">Service agreement</small></span>
      <button type="button" class="sdo-toggle" role="switch" :aria-checked="ordered" @click="setOrdered(!ordered)">
        <span>Signing order</span><i class="sdo-switch" :class="{ 'is-on': ordered }" aria-hidden="true" />
      </button>
    </div>
    <p class="sdo-hints">
      <span :class="{ 'is-off': !ordered }">In this order. Each person gets the email when the one before them has signed.</span>
      <span :class="{ 'is-off': ordered }">Everyone gets the email at the same time and can sign in any order.</span>
    </p>

    <ol class="sdo-people">
      <li v-for="(person, index) in people" :key="person.name">
        <span class="sdo-avatar" translate="no">{{ person.initials }}</span>
        <span class="sdo-who">
          <b translate="no">{{ person.name }}</b>
          <span class="sdo-mail" :class="{ 'is-sent': emailed(index) }">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path :d="mail" /></svg>
            <span class="sdo-mail-text">
              <span v-for="label in mailLabels(index)" :key="label" :class="{ 'is-off': label !== mailLabel(index) }">{{ label }}</span>
            </span>
          </span>
        </span>
        <em class="rp-chip sdo-chip" :class="chipTone(index)">
          <span v-for="label in chipLabels(index)" :key="label" :class="{ 'is-off': label !== chipLabel(index) }">{{ label }}</span>
        </em>
      </li>
    </ol>

    <div class="sdo-actions">
      <!-- One button; every label it can show sits in the same cell, so its width never changes. -->
      <button type="button" class="sdo-sign" @click="done ? reset() : sign()">
        <span v-for="person in people.slice(0, signers)" :key="person.name" :class="{ 'is-off': done || person !== people[next] }" :aria-hidden="done || person !== people[next]">
          Sign as <span translate="no">{{ person.first }}</span>
        </span>
        <span :class="{ 'is-off': !done }" :aria-hidden="!done">Start over</span>
      </button>
      <!-- The longest message sits invisibly in the same cell, so the log never changes height. -->
      <p class="sdo-log">
        <span class="sdo-log-size" aria-hidden="true">Ana Lopez signed · Invitation sent to Jordan Lee</span>
        <span class="sdo-log-size" aria-hidden="true">Signed copy sealed · Signed copies sent to everyone</span>
        <span aria-live="polite">{{ log }}</span>
      </p>
    </div>
  </div>
</template>

<script setup>
defineProps({
  chapter: { type: Object, required: true },
});

const people = [
  { name: "Ana Lopez", first: "Ana", initials: "AL", role: "Signer" },
  { name: "Jordan Lee", first: "Jordan", initials: "JL", role: "Countersigner" },
  { name: "Priya Shah", first: "Priya", initials: "PS", role: "CC" },
];
const signers = 2;
const ordered = ref(true);
const next = ref(0);
const done = computed(() => next.value >= signers);
const log = ref("Invitation sent to Ana Lopez");

// In order, the next signer is emailed only when the one before has signed; otherwise everyone at once.
// The CC gets the signed copy at the end.
const emailed = (index) => (index >= signers ? done.value : !ordered.value || index <= next.value);
const signed = (index) => index < signers && index < next.value;
const mailLabel = (index) => {
  if (index >= signers) return done.value ? "Signed copies sent to everyone" : "Gets the signed copy";
  return emailed(index) ? "Invitation sent" : "It isn't your turn yet";
};
// Every label a row can show, stacked in one cell so the row keeps the height of the longest.
const mailLabels = (index) =>
  index >= signers ? ["Gets the signed copy", "Signed copies sent to everyone"] : ["Invitation sent", "It isn't your turn yet"];
const chipLabels = (index) => (index >= signers ? ["CC"] : [people[index].role, "Signed"]);
const chipLabel = (index) => (index >= signers ? "CC" : signed(index) ? "Signed" : people[index].role);
const chipTone = (index) => (index >= signers ? "" : signed(index) ? "rp-chip--green" : "");

const setOrdered = (value) => {
  ordered.value = value;
  next.value = 0;
  log.value = value ? "Invitation sent to Ana Lopez" : "Invitation sent to Ana Lopez and Jordan Lee";
};
const sign = () => {
  const who = people[next.value];
  next.value += 1;
  if (next.value >= signers) log.value = "Signed copy sealed · Signed copies sent to everyone";
  else log.value = ordered.value ? `${who.name} signed · Invitation sent to ${people[next.value].name}` : `${who.name} signed`;
};
const reset = () => setOrdered(ordered.value);
const mail = "M20 4H4c-1.1 0-1.99.9-1.99 2L2 18c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4-8 5-8-5V6l8 5 8-5v2z";
</script>

<style scoped>
.sdo { display: grid; gap: 10px; height: auto; max-width: 520px; margin: 0 auto; padding: 14px; }
.sdo-head { display: flex; align-items: center; justify-content: space-between; gap: 10px; }
.sdo-title { display: grid; line-height: 1.3; }
.sdo-title b { font-size: 14px; font-weight: 600; }
.sdo-title small { color: var(--rp-muted); font-size: 11.5px; }
.sdo-toggle { display: inline-flex; align-items: center; gap: 8px; min-height: 44px; border: 0; background: transparent; padding: 0 4px; color: var(--rp-text); font: inherit; font-size: 12.5px; font-weight: 600; cursor: pointer; }
.sdo-toggle:focus-visible, .sdo-sign:focus-visible { outline: 2px solid #0e7490; outline-offset: 2px; }
.sdo-switch { position: relative; width: 34px; height: 20px; border-radius: 999px; background: var(--rp-grey-fill); box-shadow: inset 0 0 0 1px var(--rp-line); }
.sdo-switch::before { content: ""; position: absolute; inset: 0; border-radius: inherit; background: var(--rp-ink); opacity: 0; transition: opacity 0.2s ease; }
.sdo-switch::after { content: ""; position: absolute; top: 3px; left: 3px; width: 14px; height: 14px; border-radius: 50%; background: #fff; box-shadow: 0 1px 2px rgb(0 0 0 / 0.3); transition: transform 0.2s ease; }
.sdo-switch.is-on::before { opacity: 1; }
.sdo-switch.is-on::after { transform: translateX(14px); }
.dark .sdo-switch.is-on::after { background: #171717; }
.sdo-hints { display: grid; margin: -4px 0 0; color: var(--rp-muted); font-size: 11.5px; line-height: 1.4; }
.sdo-hints span { grid-area: 1 / 1; transition: opacity 0.2s ease; }
.sdo-hints .is-off { opacity: 0; }

.sdo-people { display: grid; margin: 0; border: 1px solid var(--rp-line); border-radius: 8px; background: var(--rp-card); padding: 0 12px; list-style: none; }
.sdo-people li { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 10px; padding: 10px 0; }
.sdo-people li + li { border-top: 1px solid var(--rp-line); }
.sdo-avatar { display: grid; place-items: center; width: 30px; height: 30px; border-radius: 50%; background: var(--rp-grey-fill); color: var(--rp-muted); font-size: 11px; font-weight: 700; }
.sdo-who { display: grid; gap: 1px; min-width: 0; }
.sdo-who b { font-size: 12.5px; font-weight: 600; }
.sdo-mail { display: flex; align-items: center; gap: 5px; min-width: 0; color: var(--rp-faint); font-size: 11px; line-height: 1.3; }
.sdo-chip { display: inline-grid; justify-items: center; }
.sdo-chip > span { grid-area: 1 / 1; }
.sdo-chip > .is-off { visibility: hidden; }
.sdo-mail-text { display: grid; min-width: 0; }
.sdo-mail-text > span { grid-area: 1 / 1; }
.sdo-mail-text > .is-off { visibility: hidden; }
.sdo-mail svg { width: 13px; height: 13px; opacity: 0.55; }
.sdo-mail.is-sent { color: var(--rp-blue); }
.sdo-mail.is-sent svg { opacity: 1; }

.sdo-actions { display: grid; grid-template-columns: auto 1fr; align-items: center; gap: 12px; }
.sdo-sign { min-height: 44px; border: 0; border-radius: 8px; background: #3f58bf; padding: 0 16px; color: #fff; font: inherit; font-size: 13px; font-weight: 600; cursor: pointer; }
.sdo-sign { display: inline-grid; place-items: center; }
.sdo-sign > span { grid-area: 1 / 1; }
.sdo-sign > .is-off { visibility: hidden; }
.dark .sdo-sign { background: #93aef9; color: #171717; }
/* Narrow phones: the role chip moves under the email status so neither is cut off. */
.sdo { container-type: inline-size; }
@container (max-width: 340px) {
  .sdo-people li { grid-template-columns: auto minmax(0, 1fr); }
  .sdo-chip { grid-column: 2; justify-self: start; margin-top: -4px; }
}
.sdo-log { display: grid; margin: 0; color: var(--rp-muted); font-size: 11.5px; line-height: 1.4; }
.sdo-log > span { grid-area: 1 / 1; }
.sdo-log-size { visibility: hidden; }
@media (prefers-reduced-motion: reduce) { .sdo-switch::before, .sdo-switch::after, .sdo-hints span { transition: none; } }
</style>
